/**
 * angular-admin-oskey-io
 *
 * @license
 * Copyright (c) 2021-2025, OSKEY SAS. All rights reserved.
 *
 * The source code is protected under international copyright law.
 * All rights reserved and protected by the copyright holders.
 *
 * The source code is confidential and only available to authorized individuals with the permission of the copyright holders.
 * If you encounter this source code and do not have permission, please contact the copyright holders and delete this file.
 */

import { Injectable, Provider, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Data, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { environment } from '@env/environment';

/**
 * Custom Title update strategy that sets the title, description and other metadata if needed.
 */
@Injectable()
class OSKTitleStrategy extends TitleStrategy {
  private readonly title: Title = inject(Title);
  private readonly meta: Meta = inject(Meta);

  private readonly defaultDescription: string =
    'Innovative access control solution for residential building';

  constructor() {
    super();
  }

  /**
   *  Overrides the updateTitle function from the {@link TitleStrategy } classs to update the additional inforamtion too.
   *
   * @param { RouterStateSnapshot } snapshot
   */
  override updateTitle(snapshot: RouterStateSnapshot): void {
    const newTitle = this.buildTitle(snapshot);
    const routeData: Data | undefined = snapshot.root.firstChild?.data;

    this.setTitle(newTitle);
    this.setDescription(routeData);
    this.setAppleItunesTag(routeData);
  }

  /**
   * Constructs and sets the page title.
   *
   * @param { string | undefined } title
   */
  setTitle(title: string | undefined): void {
    let newTitle = environment.title;
    if (title) {
      newTitle += ` | ${title}`;
    }

    this.title.setTitle(newTitle);
    this.meta.updateTag({
      name: 'title',
      content: newTitle
    });
  }

  /**
   * Sets the page description.
   *
   * @param { Data } data The route's data snapshot
   */
  setDescription(data: Data | undefined): void {
    if (data?.description) {
      this.meta.updateTag({
        name: 'description',
        content: data.description
      });
    } else {
      this.meta.updateTag({
        name: 'description',
        content: this.defaultDescription
      });
    }
  }

  /**
   * Constructs and saves the apple itunes metadata
   *
   * @param { Data } data The route's data snapshot
   */
  setAppleItunesTag(data: Data | undefined): void {
    let appItuneAppContent = `app-id=${environment.apple.appBundleId}`;
    if (data) {
      const appleAppURL: string | undefined = data.appleAppURL;
      const appleAppClipBundleId: string | undefined = data.appleAppClipBundleId;

      if (appleAppURL) {
        appItuneAppContent += `, app-url=${appleAppURL}`;
      }

      if (appleAppClipBundleId) {
        appItuneAppContent += `, app-clip-bundle-id=${environment.apple.appClipBundleIds[appleAppClipBundleId]}, app-clip-display=card`;
      }
    }
    this.meta.updateTag({
      name: 'apple-itunes-app',
      content: appItuneAppContent
    });
  }
}

export const OSKTitleStrategyProvider: Provider = {
  provide: TitleStrategy,
  useClass: OSKTitleStrategy
};
